/* eslint-disable indent */
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {MainLoader, DataGrid, ConfirmationModalPromise} from '@components';
import {GreenButton, StyledH3, WhiteButton} from '@styled';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useParams} from 'react-router-dom';
import styled from 'styled-components';
import {Breadcrumb} from 'react-bootstrap';
import {sagaActions} from './redux/saga';
import AddEditInputField from './AddEditInputField';

const StyledHeadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  .heading {
  }
  .actions {
  }
`;

const RowActions = ({data, onAction}) => {
  return (
    <div>
      <WhiteButton
        onClick={() => onAction('edit', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-edit mr-1" />
        Edit
      </WhiteButton>
      <WhiteButton
        onClick={() => onAction('delete', data)}
        style={{margin: '.16em'}}
      >
        <i className="fa fa-trash mr-1" />
        Delete
      </WhiteButton>
    </div>
  );
};

const ManageSourceInputTable = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const remoteData = useSelector((state) => state.calcEngine.sourceInputFields);
  const isLoading = useSelector(
    (state) => state.calcEngine.sourceInputFields.loading
  );
  const [editRow, setEditRow] = useState(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const history = useHistory();

  const {sourceId, categoryId} = useParams();

  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    sortOn: 'fieldLevel',
    sortDir: 'ASC',
  });

  useEffect(() => {
    if (
      remoteData.page !== pagination.page ||
      remoteData.pageSize !== pagination.pageSize ||
      remoteData.totalCount !== pagination.totalCount ||
      remoteData.sortOn !== pagination.sortOn ||
      remoteData.sortDir !== pagination.sortDir
    ) {
      setPagination({
        page: remoteData.page,
        pageSize: remoteData.pageSize,
        totalCount: remoteData.totalCount,
        sortOn: remoteData.sortOn,
        sortDir: remoteData.sortDir,
      });
    }
  }, [remoteData]);

  const fetchData = ({
    page = 1,
    pageSize = pagination.pageSize,
    sortOn,
    sortDir,
  }) => {
    dispatch({
      type: sagaActions.FETCH_INPUT_TABLE_FIELDS,
      payload: {
        sourceId,
        values: {
          page,
          pageSize,
          sortOn,
          sortDir,
        },
      },
    });
  };

  const handleDeleteField = (remove) => {
    dispatch({
      type: sagaActions.REMOVE_INPUT_TABLE_FIELD,
      payload: {
        sourceId,
        fieldId: remove.id,
        values: {},
      },
    });
  };

  const handleUpdateFieldStatus = (row) => {
    const updateStatusTo = row.status === 'Active' ? 'Inactive' : 'Active';
    dispatch({
      type: sagaActions.UPDATE_INPUT_TABLE_FIELD,
      payload: {sourceId, fieldId: row.id, values: {status: updateStatusTo}},
    });
  };

  const handleRowAction = useCallback(
    (action, row) => {
      switch (action) {
        case 'edit': {
          setEditRow(row);
          setShowAddEditModal(true);
          break;
        }
        case 'delete': {
          ConfirmationModalPromise({
            title: `Are you sure you want to delete this [${row.fieldName}] field?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleDeleteField(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }
        case 'status': {
          ConfirmationModalPromise({
            title: `Are you sure you want to make field [${
              row.status === 'Active' ? 'inactive' : 'active'
            }]?`,
            show: true,
            confirmBtn: 'Yes',
            cancelBtn: 'No',
            headTitle: 'Confirmation!',
          }).then(
            () => {
              handleUpdateFieldStatus(row);
            },
            () => {
              //  do nothing on failure
            }
          );
          break;
        }

        default:
          break;
      }
    },
    [editRow, showAddEditModal]
  );

  const handleAddField = () => {
    setShowAddEditModal(true);
  };

  const handleToggleAddEditModal = useCallback(() => {
    setEditRow(null);
    setShowAddEditModal(false);
    // refresh the list here
    fetchData({
      page: pagination.page,
      pageSize: pagination.pageSize,
      sortOn: pagination.sortOn,
      sortDir: pagination.sortDir,
    });
  }, [editRow, showAddEditModal]);

  const handleManageTableData = () => {
    history.push(
      `/calc-engine/${categoryId}/sources/${sourceId}/input-table-data`
    );
  };

  const renderAddEditModal = () => {
    return (
      <AddEditInputField
        show={showAddEditModal}
        sourceId={sourceId}
        editRow={editRow}
        toggleModal={handleToggleAddEditModal}
      />
    );
  };

  const renderRowActions = (colProps) => {
    return <RowActions data={colProps} onAction={handleRowAction} />;
  };

  const Columns = React.useMemo(
    () => [
      {
        accessor: 'id',
        Header: '#',
        className: 'grey-column d-none d-lg-block',
        width: 50,
        tooltip: false,
        hidden: true,
      },
      {
        accessor: 'fieldName',
        Header: 'Field Name',
        tooltip: true,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'fieldLevel',
        Header: 'Field Level',
        tooltip: true,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'fieldType',
        Header: 'Field Type',
        tooltip: true,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'calculationField',
        Header: 'Calculation Field',
        tooltip: true,
        className: 'd-none d-lg-block',
      },
      {
        accessor: 'status',
        Header: 'Status',
        tooltip: true,
        className: 'd-none d-lg-block',
      },
      {
        id: 'actions',
        Header: <div className="d-none d-lg-block">Actions</div>,
        accessor: renderRowActions,
        disableSortBy: true,
        disableFilters: true,
        width: 50,
      },
    ],
    [pagination]
  );

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/'}}>
            Home
          </Breadcrumb.Item>
          <Breadcrumb.Item linkAs={Link} linkProps={{to: '/calc-engine'}}>
            Source Categories
          </Breadcrumb.Item>
          <Breadcrumb.Item
            linkAs={Link}
            linkProps={{to: `/calc-engine/${categoryId}/sources`}}
          >
            Emission Sources
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Input Tables Fields</Breadcrumb.Item>
        </Breadcrumb>
        <StyledH3>{t('owner.calcEngine.pageHeading')}</StyledH3>
        <StyledHeadRow>
          <div className="heading">
            {t('owner.calcEngine.inputTableConfig.pageHeading')}
            {remoteData.source ? ` for ${remoteData.source.name}` : ''}
          </div>
          <div className="actions">
            <GreenButton className="ml-1" onClick={handleManageTableData}>
              {t('owner.calcEngine.inputTableConfig.manageTableData')}
            </GreenButton>
            <GreenButton className="ml-1" onClick={handleAddField}>
              <i className="fa fa-plus mr-1" />
              {t('owner.calcEngine.inputTableConfig.addField')}
            </GreenButton>
          </div>
        </StyledHeadRow>
        <DataGrid
          columns={Columns}
          data={remoteData.data}
          onFetchData={fetchData}
          pagination={pagination}
          filtering={false}
          expandEnabled={false}
        />
      </div>
      {renderAddEditModal()}
    </>
  );
};

export default ManageSourceInputTable;
